// V2-Player (Datacrons) embed config + token acquisition. Fifth instance of the V2-GAC embed pattern (see
// utils/v2gac.ts, which documents the security model in detail).
//
// PRODUCTION PATH (default): the token is minted SERVER-SIDE by HotService (`POST account/v2/token`, added to
// the existing PlayerAccountsController) and stamped aud="player". The signing secret never reaches the browser.
//
// ⚠️ NOTE THE ROUTE PREFIX. This app's HotService endpoint is `account/v2/token`, NOT `player/v2/token` —
// PlayerAccountsController declares [Route("account")]. The AUDIENCE is still "player", because that is the
// app's identity, not its route. Getting these two confused is the easiest mistake here.
//
// This app is PLAYER-scoped: datacrons belong to the player, not the guild, so unlike TW/TB/Guild the server
// does NOT refuse to mint a token for a guildless player — it emits an empty guildId instead.
//
// DEV FALLBACK (opt-in only): signDevPlayerToken() mints the HS256 token IN THE BROWSER with a shared secret —
// forgeable, never in production. Enable explicitly with REACT_APP_V2PLAYER_DEV_SIGN=true.

import BaseAPI from "../service/BaseAPI";

// The embedded HotUtilsV2-Player app's origin, per environment. Override with REACT_APP_V2PLAYER_URL.
export function getV2PlayerUrl(): string {
  const override = process.env.REACT_APP_V2PLAYER_URL;
  if (override) return override;

  const host = window.location.host;
  // ⚠️ PROD + STAGING point at the container app's own FQDN, not <app>.hotutils.com. Those subdomains
  // were an aspirational convention copied from the first GAC embed and were NEVER provisioned - all twelve
  // failed to resolve (verified 2026-08-12). There is no staging container app either, so staging uses the
  // PROD one, which already accepts staging.hotutils.com as a framer via its PARENT_ORIGIN allowlist.
  // 🔴 The tab is still gated OFF outside development by isV2PlayerEnabled() - fixing the URL first means the
  // wiring is correct with nothing user-visible to roll back.
  if (host === 'hotutils.com' || host === 'www.hotutils.com') return 'https://hotutils-player-prod.orangeriver-f5924f84.southcentralus.azurecontainerapps.io';
  if (host.includes('localhost')) return 'http://localhost:5177'; // local Player dev server (GAC 5173, Events 5174, TB 5175, TW 5176)
  // Deployed development site -> the deployed dev Player container app.
  if (host.startsWith('development')) return 'https://hotutils-player-dev.redmeadow-7142addd.southcentralus.azurecontainerapps.io';
  return 'https://hotutils-player-prod.orangeriver-f5924f84.southcentralus.azurecontainerapps.io';
}

export function getV2PlayerOrigin(): string {
  return new URL(getV2PlayerUrl()).origin;
}

/**
 * The V2-Datacrons tab is DEV-ONLY for now — only surfaced (tab + route) on the Development site and in local
 * dev; suppressed on staging/production until ready. Same gating rule as the other V2 embeds.
 *
 * The ROUTE and the MENU ITEM must be gated by this same function, or the tab appears where it cannot work.
 */
export function isV2PlayerEnabled(): boolean {
  const host = window.location.host;
  return host === 'development.hotutils.com' || host.startsWith('localhost');
}

/** True when the dev-only in-browser signer should be used instead of the server endpoint (local dev only). */
export function usePlayerDevSigner(): boolean {
  return process.env.REACT_APP_V2PLAYER_DEV_SIGN === 'true';
}

interface PlayerV2TokenResponse {
  token?: string;
  issuedAtUnix?: number;
  expiresAtUnix?: number;
}

/**
 * PRODUCTION PATH: ask HotService to mint the embed token (`POST account/v2/token` on
 * PlayerAccountsController). The server derives playerId from the session and signs with aud="player"; the
 * browser only receives the finished JWT.
 */
export async function fetchPlayerToken(sessionId?: string): Promise<string> {
  const resp: PlayerV2TokenResponse = await BaseAPI.postToApi('account/v2/token', { sessionId: sessionId ?? null });
  if (!resp || !resp.token) throw new Error('Player token endpoint returned no token');
  return resp.token;
}

function base64Url(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlStr(s: string): string {
  return base64Url(new TextEncoder().encode(s));
}

export interface V2PlayerTokenClaims {
  sub: string;
  sessionId?: string;
  allyCode?: string;
  playerId?: string;
  guildId?: string;
}

// The shared HS256 secret the Player BFF verifies with (PARENT_TOKEN_SECRET). Dev default matches the Player
// app's .env.example. Override with REACT_APP_V2PLAYER_DEV_SECRET. Dev fallback signer only.
function getDevSecret(): string {
  return process.env.REACT_APP_V2PLAYER_DEV_SECRET || 'dev-shared-secret-change-me';
}

/**
 * DEV-ONLY FALLBACK: mint an HS256 JWT the Player BFF will verify. aud MUST be "player" — the Player BFF is
 * configured with PARENT_TOKEN_AUDIENCE=player, so another app's token can never authorize it and vice versa.
 *
 * No dev guild id here, unlike the guild-scoped embeds: this app is player-scoped and a guildless player is
 * a legitimate case.
 */
export async function signDevPlayerToken(claims: V2PlayerTokenClaims): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const payload = {
    ...claims,
    iss: 'hotutils',
    aud: 'player',
    iat: now,
    exp: now + 60 * 60, // 1h
  };
  const header = { alg: 'HS256', typ: 'JWT' };
  const data = `${base64UrlStr(JSON.stringify(header))}.${base64UrlStr(JSON.stringify(payload))}`;

  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(getDevSecret()),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(data));
  return `${data}.${base64Url(new Uint8Array(sig))}`;
}
